import { Injectable, OnDestroy } from '@angular/core';
import {
  GetManagerOrganizationsGQL,
  GetOrganizationGQL,
} from '../../generated/types';
import { Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ModalService, ModalType } from '@onep/ng-uikit-lib/ui/modal';
import {
  ChannelConnection,
  ChannelType,
  EventBusService,
} from '@onep/front-common-lib/event-bus';
import {
  ChangeOrganizationEvent,
  OrganizationInfo,
  PortalTrackingService,
} from '@onep/front-portal-lib';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { NoOrgaModalComponent } from './no-orga-modal/no-orga-modal.component';
import { CurrentOrganizationManagementService } from './current-organization-management.service';
import { environment } from '../../environments/environment';
import { TitleService } from './title/title.service';
import { Md5 } from 'ts-md5';
import { SelectCompanyComponent } from '../manager/select-company/select-company.component';
import { ActivatedRoute, Router } from '@angular/router';
import { GladyTrackingLogLabelizedEvent } from '@onep/front-common-lib/tracking';

@Injectable()
export class CurrentOrganizationManagementImplService
  implements CurrentOrganizationManagementService, OnDestroy
{
  static readonly CURRENT_ORG_ID_STORAGE_KEY = 'current_org_id';

  readonly currentOrganizationId$ = new ReplaySubject<string>(1);

  private currentOrganizationChannel: ChannelConnection<OrganizationInfo>;

  private changeOrganizationEventChannel: ChannelConnection<ChangeOrganizationEvent>;

  private changeOrganizationSub: Subscription;

  constructor(
    private getManagerOrganizationGQL: GetManagerOrganizationsGQL,
    private getOrganizationGQL: GetOrganizationGQL,
    private eventBusService: EventBusService,
    private modalService: ModalService,
    private securityService: OidcSecurityService,
    private portalTrackingService: PortalTrackingService,
    private titleService: TitleService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.currentOrganizationChannel = this.eventBusService.connectToChannel(
      'shared-info',
      'current-organization',
      { type: ChannelType.replayLastValue }
    );

    this.changeOrganizationEventChannel = this.eventBusService.connectToChannel(
      'shared-info',
      'change-organization',
      { type: ChannelType.event }
    );
    this.changeOrganizationSub = this.changeOrganizationEventChannel.receiver
      .pipe(switchMap((event) => this.switchOrganization(event.organizationId)))
      .subscribe((changed) => {
        if (changed) {
          // Reload and go to the starting page of the portal.
          window.location.assign(window.location.origin);
        } else {
          window.location.reload();
        }
      });
  }

  ngOnDestroy(): void {
    this.changeOrganizationSub.unsubscribe();
  }

  /**
   * Compute the current organization according to the currently stored org id in localstorage
   * and the orgs the user is manager of.
   */
  computeCurrentOrganization(): Observable<string | null> {
    if (
      environment.features.organizationAsAdmin &&
      environment.organizationAsAdmin != null &&
      window.location.host.match(
        environment.organizationAsAdmin.subDomainRegex
      ) != null
    ) {
      // admin as manager is activated and the admin is accessing the manager portal on the "admin-as-manager" url.
      return this.computeCurrentOrganizationForAdminAsManager().pipe(
        map((currentOrgId) => {
          if (currentOrgId) {
            this.getOrganizationGQL
              .fetch({ currentOrgId }, { fetchPolicy: 'no-cache' })
              .subscribe((orgSubMod) => {
                const currentOrganization = orgSubMod.data.organization;
                if (currentOrganization?.mvpDistribActive) {
                  if (!currentOrganization.dtsIsOnboardingCompleted) {
                    this.goToMvpDistribExperience();
                  } else if (window.location.pathname === '/') {
                    this.goToDistributionHome();
                  }
                }
              });
          }
          return currentOrgId;
        })
      );
    } else {
      return this.computeCurrentOrganizationForManager();
    }
  }

  private goToMvpDistribExperience(): void {
    if (window.location.pathname !== '/welcome') {
      window.location.href = window.location.origin + '/welcome';
    }
  }

  private goToDistributionHome(): void {
    window.location.href =
      window.location.origin + '/services/distribution/home';
  }

  private showSelectCompanyModal(): Observable<string> {
    this.portalTrackingService.sendEvent(
      new GladyTrackingLogLabelizedEvent(
        'pageView',
        'organizationSelectionModalViewed'
      )
    );
    return this.modalService
      .show({
        component: SelectCompanyComponent,
        type: ModalType.MaxiModal,
        fullHeight: true,
        input: {
          currentOrganizationId: this.route.snapshot.data.orgId,
        },
      })
      .closed$.pipe();
  }

  private canShowSelectCompanyModal(): boolean {
    const refreshToken = window.localStorage.getItem('secret');
    return (
      environment.platform === 'manager' &&
      (!refreshToken ||
        !(refreshToken === Md5.hashStr(this.securityService.getRefreshToken())))
    );
  }

  private computeCurrentOrganizationForManager(): Observable<string | null> {
    return this.getManagerOrganizationGQL
      .fetch({}, { fetchPolicy: 'network-only', errorPolicy: 'all' })
      .pipe(
        map((result) =>
          result.data.authenticatedUser.managerOf?.slice().sort((a, b) => {
            const textA = a?.name.toLowerCase();
            const textB = b?.name.toLowerCase();
            return textA < textB ? -1 : textA > textB ? 1 : 0;
          })
        ),
        switchMap((orgsList) => {
          if (orgsList && orgsList.length > 0) {
            if (orgsList.length > 1 && this.canShowSelectCompanyModal()) {
              return this.showSelectCompanyModal();
            } else {
              const chosenOrgId = this.calculateChosenOrgId(
                orgsList.map((a) => a.id)
              );
              this.emitOrgId(chosenOrgId);
              this.saveCurrentOrgId(chosenOrgId);
              const currentOrganization = orgsList.find(
                (key) => key.id === chosenOrgId
              );
              if (currentOrganization?.mvpDistribActive) {
                if (!currentOrganization.dtsIsOnboardingCompleted) {
                  this.goToMvpDistribExperience();
                } else if (window.location.pathname === '/') {
                  this.goToDistributionHome();
                }
              }
              return of(chosenOrgId);
            }
          } else {
            // Show a modal to say that we found no orga and ask the user to logout and reconnect.
            this.modalService
              .show({
                type: ModalType.PopInModal,
                component: NoOrgaModalComponent,
              })
              .closed$.pipe(
                switchMap(() => this.securityService.logoffAndRevokeTokens())
              )
              .subscribe(() => this.securityService.authorize());
            return of(null);
          }
        })
      );
  }

  private calculateChosenOrgId(orgIds: string[]): string {
    const orgIdParameter = new URLSearchParams(window.location.search).get(
      'organizationId'
    );
    const storedOrgId = this.getCurrentOrgId();

    let chosenOrgId: string;
    if (orgIdParameter) {
      chosenOrgId = orgIdParameter;
    } else {
      if (storedOrgId != null && orgIds.indexOf(storedOrgId) !== -1) {
        chosenOrgId = storedOrgId;
      } else {
        chosenOrgId = orgIds[0];
      }
    }
    return chosenOrgId;
  }

  private computeCurrentOrganizationForAdminAsManager(): Observable<
    string | null
  > {
    // FIXME: Check the right to do "admin-as-manager" for the user.
    const orgaId = window.location.host.split('.')[0];
    const regex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (orgaId != null && regex.test(orgaId)) {
      this.saveCurrentOrgId(orgaId);
      this.emitOrgId(orgaId);
      this.titleService.updateManagerBridgeTitle();
      return of(orgaId);
    } else {
      const currentOrgaId = this.getCurrentOrgId();
      if (currentOrgaId != null) {
        this.emitOrgId(currentOrgaId);
        this.titleService.updateManagerBridgeTitle();
        return of(orgaId);
      } else {
        console.error(
          'Unable to start manager portal in "admin-as-manager" mode as no orga id was given (or saved from a previous session).'
        );
        // FIXME: Display a specific error modal.
      }
    }
    return of(null);
  }

  /**
   * Request a switch of organization.
   * Check if the requested organization id is in the list of org the user is manager of
   * and then store it in local storage and triggers a full refresh of the portal.
   * @param wantedOrgId the organization uuid to switch to.
   */
  private switchOrganization(wantedOrgId: string): Observable<boolean> {
    return this.getManagerOrganizationGQL
      .fetch({}, { fetchPolicy: 'network-only', errorPolicy: 'all' })
      .pipe(
        map((result) =>
          result.data.authenticatedUser.managerOf?.map((org) => org.id)
        ),
        switchMap((orgsList) => {
          if (orgsList && orgsList.length > 0) {
            if (orgsList.indexOf(wantedOrgId) !== -1) {
              const currentOrgId = localStorage.getItem(
                CurrentOrganizationManagementImplService.CURRENT_ORG_ID_STORAGE_KEY
              );
              this.saveCurrentOrgId(wantedOrgId);
              return of(currentOrgId !== wantedOrgId);
            } else {
              console.error(
                'Cannot switch to organization ' +
                  wantedOrgId +
                  ' as this user is not a manager of this organization!'
              );
              return of(false);
            }
          } else {
            console.error('The user is not a manager of any organization!');
            return of(false);
          }
        })
      );
  }

  private getCurrentOrgId(): string | null {
    return window.localStorage.getItem(
      CurrentOrganizationManagementImplService.CURRENT_ORG_ID_STORAGE_KEY
    );
  }

  private saveCurrentOrgId(orgId: string): void {
    window.localStorage.setItem(
      CurrentOrganizationManagementImplService.CURRENT_ORG_ID_STORAGE_KEY,
      orgId
    );
  }

  private emitOrgId(orgId: string): void {
    this.currentOrganizationId$.next(orgId);
    this.currentOrganizationChannel.emitter.next({
      currentOrganizationId: orgId,
    });
  }
}
